import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import qrcode from 'qrcode-generator';
import { useMemo } from 'react';
import { logoFor, type HouseAd } from '../ads/houseAds';

interface Props {
  ad: HouseAd;
  onClose: () => void;
  onRemoveAds: () => void;
}

/** Full-screen house ad, cross-faded over the slideshow, with a QR code to the advertiser. */
export function HouseAdSlide({ ad, onClose, onRemoveAds }: Props) {
  const logo = logoFor(ad);
  const qr = useMemo(() => {
    // Highest error correction (~30%) so the code still scans with the logo over its centre.
    const q = qrcode(0, logo ? 'H' : 'M');
    q.addData(ad.url);
    q.make();
    return q.createDataURL(6, 2);
  }, [ad.url, logo]);
  const host = (() => {
    try {
      return new URL(ad.url).hostname.replace(/^www\./, '');
    } catch {
      return ad.url;
    }
  })();

  return (
    <motion.div
      className="absolute inset-0 z-30 flex items-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.6, ease: 'easeInOut' }}
      style={{ background: ad.image ? `center / cover no-repeat url("${ad.image}")` : ad.scene }}
      role="complementary"
      aria-label={`Advertisement from ${ad.advertiser}`}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
      <div className="relative flex w-full flex-wrap items-end justify-between gap-8 p-10 text-white md:p-16">
        <div className="max-w-3xl">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] opacity-70">Ad · {ad.advertiser}</div>
          <h2 className="text-4xl font-semibold leading-tight md:text-6xl">{ad.headline}</h2>
          {ad.body && <p className="mt-4 text-lg opacity-90 md:text-2xl">{ad.body}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href={ad.url}
              target="_blank"
              rel="noopener"
              className="rounded-full bg-white px-6 py-3 font-medium text-black transition hover:bg-white/90"
            >
              {ad.cta} →
            </a>
            <button onClick={onRemoveAds} className="rounded-full border border-white/40 px-5 py-3 text-sm hover:bg-white/10">
              Remove ads forever – £10 + tax
            </button>
          </div>
        </div>
        {/* Scan to open the advertiser's site on a phone – TVs can't follow links. */}
        <div className="relative h-32 w-32 shrink-0 md:h-40 md:w-40">
          <img src={qr} alt={`QR code for ${host}`} className="h-full w-full rounded-xl bg-white p-1.5 [image-rendering:pixelated]" />
          {logo && (
            // ~22% of the code's width: well inside what level-H error correction recovers.
            <img
              src={logo}
              alt=""
              className="absolute left-1/2 top-1/2 h-[22%] w-[22%] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-[2px]"
            />
          )}
        </div>
      </div>
      <button
        onClick={onClose}
        className="absolute right-6 top-6 rounded-full bg-black/40 p-2 text-white hover:bg-black/60"
        aria-label="Close advertisement"
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
    </motion.div>
  );
}
